import React from 'react';
import { Award, Users, ThumbsUp, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-16 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About Legacy Motorplex
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              For over 15 years, we've been Marietta's trusted source for affordable luxury vehicles, 
              building lasting relationships with our community through quality, integrity, and exceptional service.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  Legacy Motorplex was founded with a simple mission: to make luxury vehicles accessible 
                  to everyone. We believe that quality, comfort, and style shouldn't be exclusive to the elite.
                </p>
                <p>
                  Over the past 15+ years, we've helped thousands of customers find their dream vehicles. 
                  Our carefully curated inventory features only the finest pre-owned luxury cars from brands 
                  like BMW, Mercedes-Benz, Audi, Lexus, Tesla, and more.
                </p>
                <p>
                  What sets us apart is our commitment to transparency, quality, and customer satisfaction. 
                  Every vehicle in our inventory undergoes rigorous inspection to ensure it meets our high standards.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="bg-teal-600 text-white">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold mb-2">15+</div>
                  <div className="text-sm">Years in Business</div>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 text-white">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold mb-2">1000+</div>
                  <div className="text-sm">Happy Customers</div>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 text-white">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold mb-2">100%</div>
                  <div className="text-sm">Quality Guarantee</div>
                </CardContent>
              </Card>
              <Card className="bg-teal-600 text-white">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold mb-2">5★</div>
                  <div className="text-sm">Customer Rating</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-none shadow-md hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Quality First</h3>
                <p className="text-gray-600">
                  Every vehicle is thoroughly inspected and meets our rigorous quality standards.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ThumbsUp className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Integrity</h3>
                <p className="text-gray-600">
                  Transparent pricing and honest communication in every transaction.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Customer Focus</h3>
                <p className="text-gray-600">
                  Your satisfaction is our top priority. We're here to help every step of the way.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Continuous Growth</h3>
                <p className="text-gray-600">
                  Always improving our services and expanding our selection for you.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
              Why Choose Legacy Motorplex?
            </h2>
            <div className="space-y-6">
              <Card className="border-l-4 border-l-teal-600">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Experience You Can Trust</h3>
                  <p className="text-gray-700">
                    With over 15 years in the luxury vehicle market, we have the knowledge and expertise 
                    to help you find exactly what you're looking for.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-teal-600">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Carefully Selected Inventory</h3>
                  <p className="text-gray-700">
                    We hand-pick each vehicle in our inventory, ensuring only the best quality luxury cars 
                    make it to our showroom.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-teal-600">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Competitive Pricing</h3>
                  <p className="text-gray-700">
                    We offer affordable luxury with transparent pricing and flexible financing options to 
                    fit your budget.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-teal-600">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Exceptional Service</h3>
                  <p className="text-gray-700">
                    From your first visit to long after your purchase, we're committed to providing 
                    outstanding customer service and support.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Visit Our Showroom
            </h2>
            <p className="text-xl text-gray-600">
              Come see our inventory in person at our Marietta location
            </p>
          </div>
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <div className="space-y-4">
                <div>
                  <p className="text-2xl font-bold text-gray-900 mb-2">Legacy Motorplex</p>
                  <p className="text-lg text-gray-700">2759 DELK RD SE</p>
                  <p className="text-lg text-gray-700">MARIETTA, GA 30067</p>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-lg font-semibold text-gray-900 mb-1">Call us:</p>
                  <a href="tel:4047231672" className="text-2xl font-bold text-teal-600 hover:text-teal-700">
                    (404) 723-1672
                  </a>
                </div>
                <div className="pt-4">
                  <p className="text-gray-600 font-semibold">Monday - Saturday</p>
                  <p className="text-gray-600">By Appointment Only</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
